import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Bookmark, BookmarkCheck, Wifi, Zap, Share2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { formatJobLocation } from "../utils/formatLocation";
import { shareJob } from "../utils/shareJob";
import { jobTypeLabel } from "../config/jobTaxonomy";
import { track } from "../lib/analytics";
import { BadgeCheck } from "lucide-react";
import { AdzunaAttribution } from "./AdzunaAttribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { USD: "US$", EUR: "€", GBP: "£", BRL: "R$", MXN: "$", ARS: "AR$", UYU: "$U" };
export const SUPPORTED_CURRENCIES = ["EUR", "USD", "UYU", "ARS", "BRL"];
const SALARY_PERIOD_SUFFIX_KEY = { hour: "perHour", month: "perMonth", year: "perYear" };

export function formatSalary(min, max, currency = "USD", upToLabel = "Up to") {
  const sym = CURRENCY_SYMBOLS[currency] || `${currency} `;
  const fmt = (n) => (n >= 1000 ? `${sym}${Math.round(n / 1000)}K` : `${sym}${n}`);
  if (min && max) return `${fmt(min)} – ${fmt(max)}`;
  if (min) return `${fmt(min)}+`;
  if (max) return `${upToLabel} ${fmt(max)}`;
  return "";
}

export function formatDistance(distanceKm) {
  if (distanceKm === null || distanceKm === undefined) return null;
  if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m`;
  const rounded = Math.round(distanceKm * 10) / 10;
  return rounded % 1 === 0 ? `${Math.round(rounded)} km` : `${rounded.toFixed(1).replace(".", ",")} km`;
}

export function timeAgoRaw(dateStr) {
  if (!dateStr) return "";
  return Math.floor((Date.now() - new Date(dateStr)) / 86400000);
}

export default function JobCard({ job, onSaveToggle, compact = false, showMatchScore = false }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved]   = React.useState(job.is_saved || false);
  const isExternal = job.source === "adzuna";

  // Atribucion obligatoria por Terminos de Adzuna — impresion del aviso externo.
  React.useEffect(() => {
    if (isExternal) {
      track("external_job_impression", { provider: "adzuna", external_id: job.external_id, country: job.country_code, city: job.location });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardPad      = compact ? "12px" : "14px";
  const gapSize      = compact ? "10px" : "12px";
  const avatarSize   = compact ? 26 : 36;
  const titleSize    = compact ? "14px" : "18px";
  const titleClamp   = compact ? 1 : 2;
  const salarySize   = compact ? "14px" : "17px";
  const tagPad       = compact ? "3px 8px" : "4px 10px";
  const tagFont      = compact ? "11px" : "12px";
  // Touch targets for Save/Share — min 40x40 (compact), ideal 44x44 (regular),
  // icon itself stays visually the same size, only the tappable area grows.
  const touchSize    = compact ? 40 : 44;
  const iconGap      = compact ? "8px" : "10px";

  React.useEffect(() => { setSaved(job.is_saved || false); }, [job.is_saved]);

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const d = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
    if (d === 0) return t("jobCard.today");
    if (d === 1) return t("jobCard.yesterday");
    if (d < 7)  return `${d}${t("jobCard.dAgo")}`;
    if (d < 30) return `${Math.floor(d / 7)}${t("jobCard.wAgo")}`;
    return `${Math.floor(d / 30)} ${t("jobCard.moAgo")}`;
  };

  const handleSave = async (e) => {
    e.preventDefault(); e.stopPropagation();
    // Guest: same redirect-to-/auth flow as JobDetailPage/map popup — never call
    // the protected endpoint, never flip the button into a false "saved" state.
    if (!user) { navigate("/auth"); return; }
    if (saving) return;
    setSaving(true);
    try {
      if (saved) {
        await axios.delete(`${API}/saved-jobs/${job.job_id}`, { headers: getAuthHeaders() });
        setSaved(false);
        track("job_saved", { job_id: job.job_id, saved: false });
      } else {
        await axios.post(`${API}/saved-jobs/${job.job_id}`, {}, { headers: getAuthHeaders() });
        setSaved(true);
        track("job_saved", { job_id: job.job_id, saved: true });
      }
      if (onSaveToggle) onSaveToggle(job.job_id, !saved);
    } catch {}
    finally { setSaving(false); }
  };

  const handleShare = (e) => {
    e.preventDefault(); e.stopPropagation();
    shareJob(job, formatJobLocation(job), t("share.intro"), () => toast.success(t("share.linkCopied")));
  };

  const todayStr  = new Date().toISOString().slice(0, 10);

  return (
    <Link
      to={`/jobs/${job.job_id}`}
      data-testid={`job-card-${job.job_id}`}
      className="card-job group block"
      style={{ padding: cardPad }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: gapSize }}>

        {/* ── FILA 1: logo + empresa (izq) | compartir + guardar (der) ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
            {/* Avatar */}
            <div
              style={{
                width: avatarSize, height: avatarSize, borderRadius: 10, flexShrink: 0, overflow: "hidden",
                background: job.company_logo ? "#F1F5F9" : "linear-gradient(135deg, rgba(2,140,248,0.1), rgba(245,95,165,0.1))",
                border: "1px solid rgba(2,140,248,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "Poppins, sans-serif", fontWeight: 700,
                color: "#028CF8", fontSize: compact ? "12px" : "14px",
              }}
              data-testid="job-card-company-avatar"
            >
              {job.company_logo ? (
                <img src={job.company_logo} alt={job.company_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                job.company_name?.[0]?.toUpperCase() || "J"
              )}
            </div>

            {/* Company name + verificación básica */}
            <span
              style={{
                fontSize: "14px", color: "#475569", fontWeight: 500,
                fontFamily: "Poppins, sans-serif", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                display: "flex", alignItems: "center", gap: "4px",
              }}
            >
              {job.company_name}
              {job.company_verified && (
                <BadgeCheck data-testid="job-card-verified-badge" size={14} style={{ color: "#059669", flexShrink: 0 }} title="Verificación básica" />
              )}
            </span>
          </div>

          {/* Share + Bookmark — 40-44px tappable area, icon stays visually contained.
              Adzuna: sin Guardar (favoritos externos son fase futura) + atribucion
              obligatoria "Jobs by Adzuna" (Terminos de Adzuna: por-aviso, hyperlinked). */}
          <div style={{ display: "flex", alignItems: "center", gap: iconGap, flexShrink: 0, marginLeft: "8px" }}>
            <button
              onClick={handleShare}
              data-testid={`share-job-${job.job_id}`}
              aria-label={t("jobCard.shareAria")}
              title={t("jobCard.shareAria")}
              style={{
                width: touchSize, height: touchSize, display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: "10px",
                background: "transparent", color: "#64748B",
                border: "none", cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#028CF8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#64748B"; }}
            >
              <Share2 style={{ width: 16, height: 16 }} />
            </button>
            {isExternal ? (
              <AdzunaAttribution testId={`adzuna-attribution-${job.job_id}`} size="compact" />
            ) : (
              <button
                onClick={handleSave}
                data-testid={`save-job-${job.job_id}`}
                aria-label={t("jobCard.saveAria") || "Guardar"}
                style={{
                  width: touchSize, height: touchSize, display: "flex", alignItems: "center", justifyContent: "center",
                  borderRadius: "10px",
                  background: saved ? "rgba(2,140,248,0.10)" : "transparent",
                  color: saved ? "#028CF8" : "#64748B",
                  border: "none", cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => { if (!saved) e.currentTarget.style.color = "#028CF8"; }}
                onMouseLeave={(e) => { if (!saved) e.currentTarget.style.color = "#64748B"; }}
              >
                {saved
                  ? <BookmarkCheck style={{ width: 16, height: 16 }} />
                  : <Bookmark      style={{ width: 16, height: 16 }} />
                }
              </button>
            )}
          </div>
        </div>

        {/* ── FILA 2: título/cargo (izq) | % match (der, solo si existe) ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
          <h3
            className="group-hover:text-[#028CF8]"
            style={{
              fontSize: titleSize, fontWeight: 600, color: "#0F172A",
              lineHeight: 1.3, margin: 0, fontFamily: "Poppins, sans-serif",
              display: "-webkit-box", WebkitLineClamp: titleClamp,
              WebkitBoxOrient: "vertical", overflow: "hidden",
              wordBreak: "break-word", overflowWrap: "anywhere",
              transition: "color 0.2s ease", minWidth: 0, flex: "1 1 auto",
            }}
          >
            {job.title}
          </h3>
          {job.match_score && (!compact || showMatchScore) && (
            <span
              data-testid={`job-match-score-${job.job_id}`}
              style={{
                flexShrink: 0, marginTop: "2px",
                fontSize: "11px", fontWeight: 700, fontFamily: "Poppins, sans-serif",
                padding: "3px 9px", borderRadius: "999px",
                background: job.match_score >= 80 ? "rgba(16,185,129,0.12)" : job.match_score >= 65 ? "rgba(2,140,248,0.10)" : "rgba(100,116,139,0.08)",
                color: job.match_score >= 80 ? "#059669" : job.match_score >= 65 ? "#028CF8" : "#64748B",
              }}
            >
              {job.match_score >= 80 ? "★" : "●"} {job.match_score}% {t("jobCard.match")}
            </span>
          )}
        </div>

        {/* ── FILA 3: salario · modalidad + badges compactos (urgente / remoto-híbrido) ──
             Se omite por completo si no hay nada real que mostrar (nunca se inventa
             salario/modalidad para empleos externos sin esos datos). ── */}
        {(() => {
          const hasSalary = job.salary_visible !== false && (job.salary_min || job.salary_max);
          const hasJobType = !!job.job_type;
          const hasUrgency = job.is_urgent || job.start_date === todayStr;
          const hasWorkModeBadge = job.work_mode === "hybrid" || (job.work_mode ? job.work_mode === "remote" : job.remote);
          const showSalaryPlaceholder = !compact && !hasSalary && !isExternal;
          if (!hasSalary && !hasJobType && !hasUrgency && !hasWorkModeBadge && !showSalaryPlaceholder) return null;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", rowGap: "6px" }}>
              {(hasSalary || hasJobType || showSalaryPlaceholder) && (
                <span style={{ fontFamily: "Poppins, sans-serif", minWidth: 0 }}>
                  {hasSalary ? (
                    <>
                      <span style={{ fontSize: salarySize, fontWeight: 700, color: "#0F172A" }}>
                        {formatSalary(job.salary_min, job.salary_max, job.salary_currency, t("jobCard.upTo"))}
                      </span>
                      {!compact && job.salary_period && (
                        <span style={{ fontSize: "12px", fontWeight: 400, color: "#94A3B8", marginLeft: "4px" }}>
                          {t(`jobCard.${SALARY_PERIOD_SUFFIX_KEY[job.salary_period] || "perYear"}`)}
                        </span>
                      )}
                      {hasJobType && <span style={{ fontSize: tagFont, color: "#94A3B8", margin: "0 4px" }}>·</span>}
                    </>
                  ) : showSalaryPlaceholder ? (
                    <span style={{ fontSize: "13px", color: "#94A3B8", fontStyle: "italic" }}>
                      {t("jobCard.salaryNotSpecified")}
                    </span>
                  ) : null}
                  {hasJobType && (
                    <span style={{ fontSize: tagFont, fontWeight: 500, color: "#64748B" }}>
                      {jobTypeLabel(job.job_type, t)}
                    </span>
                  )}
                </span>
              )}

              {hasUrgency && (
                <span
                  data-testid={`urgency-badge-${job.job_id}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "4px", flexShrink: 0,
                    fontSize: "10px", fontWeight: 700, fontFamily: "Poppins, sans-serif",
                    padding: "3px 9px", borderRadius: "999px", letterSpacing: "0.03em",
                    backgroundColor: job.is_urgent ? "rgba(245,95,165,0.12)" : "rgba(2,140,248,0.10)",
                    color: job.is_urgent ? "#F55FA5" : "#028CF8",
                  }}
                >
                  <Zap style={{ width: 10, height: 10 }} />
                  {job.is_urgent
                    ? t("jobDetail.urgentBadge")
                    : job.urgency_label === "INMEDIATA"
                    ? t("jobDetail.immediateBadge")
                    : job.urgency_label || t("jobDetail.todayBadge")}
                </span>
              )}

              {job.work_mode === "hybrid" && (
                <span
                  style={{
                    fontSize: tagFont, fontWeight: 600, fontFamily: "Poppins, sans-serif",
                    padding: tagPad, borderRadius: "999px", flexShrink: 0,
                    backgroundColor: "transparent", color: "#028CF8",
                    border: "1.5px solid #028CF8", display: "flex", alignItems: "center", gap: "4px",
                  }}
                >
                  {t("jobCard.hybrid")}
                </span>
              )}
              {(job.work_mode ? job.work_mode === "remote" : job.remote) && (
                <span
                  style={{
                    fontSize: tagFont, fontWeight: 600, fontFamily: "Poppins, sans-serif",
                    padding: tagPad, borderRadius: "999px", flexShrink: 0,
                    backgroundColor: "transparent",
                    color: "#028CF8",
                    border: "1.5px solid #028CF8",
                    display: "flex", alignItems: "center", gap: "4px",
                  }}
                >
                  <Wifi style={{ width: 11, height: 11 }} /> {t("jobCard.remote")}
                </span>
              )}
            </div>
          );
        })()}

        {/* ── FILA 4: ubicación (izq) | antigüedad/publicación (der) ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0, flex: "1 1 auto" }}>
            <MapPin style={{ width: 13, height: 13, color: "#94A3B8", flexShrink: 0 }} />
            <span
              style={{
                fontSize: "12.5px", color: "#64748B", fontFamily: "Poppins, sans-serif",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
              }}
            >
              {formatJobLocation(job) || (job.work_mode === "remote" ? t("jobCard.remote") : "")}
            </span>
            {job.distance_km !== undefined && job.distance_km !== null && (
              <span
                data-testid={`job-distance-${job.job_id}`}
                style={{
                  fontSize: "11px", fontWeight: 700, color: "#028CF8", fontFamily: "Poppins, sans-serif",
                  background: "rgba(2,140,248,0.08)", borderRadius: "999px", padding: "2px 8px", flexShrink: 0,
                }}
              >
                {formatDistance(job.distance_km)}
              </span>
            )}
          </span>

          <span style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
            <Clock style={{ width: 12, height: 12, color: "#94A3B8" }} />
            <span style={{ fontSize: "12px", color: "#64748B", fontFamily: "Poppins, sans-serif" }}>
              {getTimeAgo(job.posted_date)}
            </span>
          </span>
        </div>

      </div>
    </Link>
  );
}

export { formatSalary as default_formatSalary };
